import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

const navigationStorageKeys = [
  'paneStack',
  'selectedTags',
  'selectedSessions',
  'selectedGroups',
  'selectedSeason',
  'selectedClubPane',
  'selectedClub',
  'selectedSession',
  'selectedActivity',
  'selectedTryout',
  'selectedNav',
  'selectedEpisode',
  'selectedSpace',
  'selectorContext',
  'selectionList',
  'allowMultipleSelection',
  'maxSelections',
  'selectedParticipant',
  'selectedAppQuestion',
  'selectedQuestionOptions',
]

const readStoredValue = (key: string, fallback = '') => {
  const value = localStorage.getItem(key)
  if (value === null || value === 'null' || value === 'undefined') return fallback
  return value
}

const writeStoredValue = (key: string, value: string | number | boolean | null) => {
  if (value === null) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, String(value))
}

const readStoredList = (key: string): string[] => {
  const value = readStoredValue(key, '')
  if (!value) return []

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : [value]
  } catch {
    return [value]
  }
}

const writeStoredList = (key: string, value: string[]) => {
  localStorage.setItem(key, JSON.stringify(value))
}

export const useNavigatorStore = defineStore('navigator', () => {
  const selectedClub = ref(readStoredValue('selectedClub'))
  const selectedSeason = ref(readStoredValue('selectedSeason'))
  const selectedClubPane = ref(readStoredValue('selectedClubPane', 'Home'))
  const selectedTryout = ref(readStoredValue('selectedTryout'))
  const selectedSession = ref(readStoredValue('selectedSession', '0'))
  const selectedParticipant = ref(readStoredValue('selectedParticipant', '0'))
  const selectedActivity = ref(readStoredValue('selectedActivity'))
  const selectedEpisode = ref(readStoredValue('selectedEpisode'))
  const selectedSpace = ref(readStoredValue('selectedSpace', 'nospace'))
  const selectedNav = ref(readStoredValue('selectedNav', 'Club'))
  const selectorContext = ref(readStoredValue('selectorContext', 'individual'))
  const selectionList = ref<string[]>(readStoredList('selectionList'))
  const paneStack = ref<string[]>(readStoredList('paneStack'))

  const selectedClubId = computed(() => selectedClub.value || null)
  const selectedEventId = computed(() => selectedTryout.value || null)
  const selectedTeamId = ref<string | null>(readStoredValue('selectedTeamId') || null)

  const resetEventSelection = () => {
    selectedTryout.value = ''
    selectedSession.value = '0'
    selectedParticipant.value = '0'
    selectedActivity.value = ''
    selectedEpisode.value = ''
    selectedSpace.value = 'nospace'
    selectionList.value = []

    writeStoredValue('selectedTryout', '')
    writeStoredValue('selectedSession', '0')
    writeStoredValue('selectedParticipant', '0')
    writeStoredValue('selectedActivity', '')
    writeStoredValue('selectedEpisode', null)
    writeStoredValue('selectedSpace', 'nospace')
    writeStoredList('selectionList', [])
  }

  const selectClub = (clubId: string) => {
    selectedClub.value = clubId
    selectedSeason.value = ''
    selectedClubPane.value = 'Home'
    selectedNav.value = 'Club'
    selectorContext.value = 'individual'
    resetEventSelection()

    writeStoredValue('selectedClub', clubId)
    writeStoredValue('selectedSeason', '')
    writeStoredValue('selectedClubPane', 'Home')
    writeStoredValue('selectedNav', 'Club')
    writeStoredValue('selectorContext', 'individual')
  }

  const justSelectClub = (clubId: string) => {
    selectedClub.value = clubId
    writeStoredValue('selectedClub', clubId)
  }

  const selectSeason = (seasonId: string) => {
    selectedSeason.value = seasonId
    writeStoredValue('selectedSeason', seasonId)
    resetEventSelection()
  }

  const selectTryout = (tryoutId: string) => {
    selectedTryout.value = tryoutId
    writeStoredValue('selectedTryout', tryoutId)
    selectedSession.value = '0'
    selectedParticipant.value = '0'
    selectedSpace.value = 'nospace'
    selectionList.value = []
    writeStoredValue('selectedSession', '0')
    writeStoredValue('selectedParticipant', '0')
    writeStoredValue('selectedSpace', 'nospace')
    writeStoredList('selectionList', [])
  }

  const selectPane = (pane: string) => {
    paneStack.value.push(selectedClubPane.value)
    selectedClubPane.value = pane
    writeStoredValue('selectedClubPane', pane)
    writeStoredList('paneStack', paneStack.value)
  }

  const lastPane = () => {
    selectedClubPane.value = paneStack.value.pop() || 'Home'
    writeStoredValue('selectedClubPane', selectedClubPane.value)
    writeStoredList('paneStack', paneStack.value)
  }

  const clearPaneHistory = () => {
    paneStack.value = []
    writeStoredList('paneStack', [])
  }

  const selectNav = (navName: string) => {
    selectedNav.value = navName
    writeStoredValue('selectedNav', navName)
  }

  const setSelectedSession = (sessionId: string | number) => {
    selectedSession.value = String(sessionId)
    writeStoredValue('selectedSession', selectedSession.value)
  }

  const setSelectedParticipant = (participantId: string | number | null) => {
    selectedParticipant.value = participantId === null ? '0' : String(participantId)
    writeStoredValue('selectedParticipant', selectedParticipant.value)
  }

  const setSelectedTeam = (teamId: string | null) => {
    selectedTeamId.value = teamId
    writeStoredValue('selectedTeamId', teamId)
  }

  const clearNavigationState = () => {
    navigationStorageKeys.forEach((key) => localStorage.removeItem(key))

    selectedClub.value = ''
    selectedSeason.value = ''
    selectedClubPane.value = 'Home'
    selectedTryout.value = ''
    selectedSession.value = '0'
    selectedParticipant.value = '0'
    selectedActivity.value = ''
    selectedEpisode.value = ''
    selectedSpace.value = 'nospace'
    selectedNav.value = 'Club'
    selectorContext.value = 'individual'
    selectionList.value = []
    paneStack.value = []
    selectedTeamId.value = null
    localStorage.removeItem('selectedTeamId')
  }

  const setSelectedClub = selectClub
  const setSelectedEvent = selectTryout
  const clearSelection = clearNavigationState

  return {
    selectedClub,
    selectedSeason,
    selectedClubPane,
    selectedTryout,
    selectedSession,
    selectedParticipant,
    selectedActivity,
    selectedEpisode,
    selectedSpace,
    selectedNav,
    selectorContext,
    selectionList,
    paneStack,
    selectedClubId,
    selectedEventId,
    selectedTeamId,
    selectClub,
    justSelectClub,
    selectSeason,
    selectTryout,
    selectPane,
    lastPane,
    clearPaneHistory,
    selectNav,
    setSelectedSession,
    setSelectedParticipant,
    setSelectedTeam,
    clearNavigationState,
    setSelectedClub,
    setSelectedEvent,
    clearSelection,
  }
})
